import { CurrenciesResponse } from '@/types/sso';
import { useMemo } from 'react';
import { useSso } from '../providers/SSOProvider';
import { getCurrency } from './useCurrency';

export interface CurrencyOption {
    label: string;
    name: string;
    value: number;
}

export interface UseCurrencyOptionsReturn {
    currenciesData: CurrenciesResponse[];
    isCurrenciesLoading: boolean;
    cryptoDepositCcyOptions: CurrencyOption[];
    cryptoWithdrawalCcyOptions: CurrencyOption[];
    fiatDepositCcyOptions: CurrencyOption[];
    fiatWithdrawalCcyOptions: CurrencyOption[];
    transferCcyOptions: CurrencyOption[];
}

const transformToOutput = (currencies: CurrenciesResponse[]) => {
    return currencies.map((currency) => {
        const { show } = getCurrency(currency.digitalAssetName || currency.name);

        return {
            label: show,
            name: show,
            value: currency.id
        };
    });
};

const useCurrencyOptions = (selectedTransferType?): UseCurrencyOptionsReturn => {
    const { currenciesApi } = useSso();

    const { data: currencies, isLoading: isCurrenciesLoading } = currenciesApi;

    const currenciesData = useMemo(() => currencies || [], [currencies]);

    const cryptoDepositCcyOptions = useMemo(() => {
        return transformToOutput(
            currenciesData.filter((currency) => currency.enabledForDeposit && currency.type === 'CRYPTO')
        );
    }, [currenciesData]);

    const cryptoWithdrawalCcyOptions = useMemo(() => {
        return transformToOutput(
            currenciesData.filter((currency) => currency.enabledForWithdrawal && currency.type === 'CRYPTO')
        );
    }, [currenciesData]);

    const fiatDepositCcyOptions = useMemo(() => {
        return transformToOutput(
            currenciesData.filter((currency) => currency.enabledForDeposit && currency.type === 'FIAT')
        );
    }, [currenciesData]);

    const fiatWithdrawalCcyOptions = useMemo(() => {
        return transformToOutput(
            currenciesData.filter((currency) => currency.enabledForWithdrawal && currency.type === 'FIAT')
        );
    }, [currenciesData]);

    const transferCcyOptions = useMemo(() => {
        return transformToOutput(
            currenciesData.filter(
                (currency) => currency.enabledForWithdrawal && currency.type === selectedTransferType?.value
            )
        );
    }, [currenciesData, selectedTransferType]);

    return {
        currenciesData,
        isCurrenciesLoading,
        cryptoDepositCcyOptions,
        cryptoWithdrawalCcyOptions,
        fiatDepositCcyOptions,
        fiatWithdrawalCcyOptions,
        transferCcyOptions
    };
};

export default useCurrencyOptions;
